import { apiUrl } from "../api/endpoints";
import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useAuth } from "../hooks/AuthProvider";
import Spinner from "../Components/Spinner";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import axios from "axios";

import "../widgets/widget.css";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import DataGridTable from "../Components/DataGridTable";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { Tooltip } from "@mui/material";
import { AssetIcon } from "../Icons";

import { LanguageContext } from "../context/LanguageContext";
import assetTranslations from "../languages/asset.json";

import { getErrorMessage } from "../utils/errorUtils";
import { AddAssetCard } from "./AddAssetCard";
import { EditAssetCard } from "./EditAssetCard";
import AssetDeviceDetailsConfigModal from "./AssetDeviceDetailsConfigModal";
import { deleteAssetById, deleteMultiAssetCards } from "../lib/actions";
import AppDialog from "../Components/AppDialog"; 
import { normalizeAssetCardListRow } from "../utils/listRowIds";

export const AssetCards = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [addAssetCard, setAddAssetCard] = useState(false);
    const [editAssetCardId, setEditAssetCardId] = useState(null);
    const [assetSummarySettingsOpen, setAssetSummarySettingsOpen] = useState(false);
    const [assetPolicyAdjustment, setAssetPolicyAdjustment] = useState(null);
    const [importingAssets, setImportingAssets] = useState(false);
    const [sheetSyncStatus, setSheetSyncStatus] = useState({ state: "idle", lastSyncedAt: null });
    const importInputRef = useRef(null);
    const sheetSyncVersionRef = useRef(0);
    const navigate = useNavigate();

    const { lang, t } = useContext(LanguageContext);
    const assetT = assetTranslations[lang];
    const isRtl = ["ar", "ur"].includes(lang);
    
    const resolverRef = useRef(null);
    const [dialog, setDialog] = useState({
        open: false,
        type: "alert", // "alert" | "confirm"
        variant: "info", // "info" | "danger" | "error"
        icon: "alert", // "alert" | "delete"
        title: "",
        message: "",
        confirmText: "Ok",
        cancelText: "Cancel",
        showCancel: false,
    });

    const closeDialog = useCallback((result = false) => {
        setDialog((d) => ({ ...d, open: false }));
        if (resolverRef.current) {
            resolverRef.current(result);
            resolverRef.current = null;
        }
    }, []);

    const alert = useCallback(
        (opts) =>
            new Promise((resolve) => {
                resolverRef.current = resolve;

                setDialog({
                    open: true,
                    type: opts?.type || "alert",
                    variant: opts?.variant || "info",
                    icon: opts?.icon || "alert",
                    title: opts?.title || "",
                    message: opts?.message || "",
                    confirmText: opts?.confirmText || "Ok",
                    cancelText: opts?.cancelText || "Cancel",
                    showCancel: Boolean(opts?.showCancel),
                });
            }),
        []
    );

    const showAlert = useCallback(
        async (type, message) => {
            const variant =
                type === "error" ? "error" : type === "warning" ? "info" : "info";

            await alert({
                type: "alert",
                variant,
                icon: "alert",
                title:
                    type === "error"
                        ? (assetT?.alert?.errorTitle || "Error")
                        : type === "warning"
                            ? (assetT?.alert?.warningTitle || "Warning")
                            : (assetT?.alert?.infoTitle || "Info"),
                message: message || "",
                confirmText: "Ok",
            });
        },
        [alert, assetT?.alert?.errorTitle, assetT?.alert?.warningTitle, assetT?.alert?.infoTitle]
    );

    const { permissions, org, user } = useAuth();
    const pagingoptions = [10, 20, 50, 100];
    const canCreateAssets = permissions["create_assets"];
    const canEditAssets = permissions["edit_assets"];
    const canDeleteAssets = permissions["delete_assets"];
    const canViewAssets = permissions["view_assets"];
    const userRole = String(user?.role || "").toLowerCase();
    const canManageAssetSummary =
        permissions["manage_asset_device_details_summary"] || userRole === "owner" || userRole === "super";

    /* ------------------------------------------------------------------ */
    /* COLUMNS                                                             */
    /* ------------------------------------------------------------------ */

    const baseColumns = [
        {
            field: "deviceName",
            headerName: assetT.column.deviceName,
            width: 150,
            align: "left",
            headerAlign: "left",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive",
        },
        {
            field: "asset_name",
            headerName: assetT.column.assetName,
            width: 200,
            align: "left",
            headerAlign: "left",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "asset_code",
            headerName: assetT.column.assetCode,
            width: 120,
            align: "center",
            headerAlign: "center",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "category_subcategory",
            headerName: "Category",
            width: 200,
            align: "left",
            headerAlign: "left",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "brand",
            headerName: assetT.column.brand,
            width: 100,
            align: "left",
            headerAlign: "left",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "model_number",
            headerName: assetT.column.modelNumber,
            width: 120,
            align: "center",
            headerAlign: "center",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "serial_number",
            headerName: assetT.column.serialNumber,
            width: 150,
            align: "center",
            headerAlign: "center",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "type",
            headerName: "Asset Type",
            width: 100,
            align: "left",
            headerAlign: "left",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "location",
            headerName: assetT.column.location,
            width: 200,
            align: "left",
            headerAlign: "left",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "manufacture_date",
            headerName: assetT.column.manufactureDate,
            width: 150,
            align: "center",
            headerAlign: "center",
            type: "date",
            valueFormatter: value =>
                value ? moment(value).format("DD/MM/YYYY HH:mm:ss") : "",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "capacity_rating",
            headerName: assetT.column.capacityRating,
            width: 120,
            align: "center",
            headerAlign: "center",
            type: "number",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "running_hours_counter",
            headerName: assetT.column.runningHoursCounter,
            width: 150,
            align: "center",
            headerAlign: "center",
            type: "number",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "condition_status",
            headerName: assetT.column.conditionStatus,
            width: 150,
            align: "center",
            headerAlign: "center",
            type: "string",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "purchase_cost",
            headerName: assetT.column.purchaseCost,
            width: 120,
            align: "right",
            headerAlign: "center",
            type: "number",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        }
    ];

    const canShowActionsColumn = canEditAssets || canDeleteAssets || canManageAssetSummary;

    const columns = canShowActionsColumn
        ? [
              {
                  field: "actions",
                  headerName: assetT.column.actions,
                  type: "actions",
                  width: 100,
                  getActions: params =>
                      [
                          canEditAssets ? (
                              <GridActionsCellItem
                                  key="edit"
                                  icon={
                                      <Tooltip title={assetT.tooltipEdit}>
                                          <EditIcon
                                              style={{
                                                  fontSize:
                                                      "calc(var(--body-font-size) * var(--font-size-multiplier))"
                                              }}
                                          />
                                      </Tooltip>
                                  }
                                  showInMenu
                                  label={assetT.tooltipEdit}
                                  onClick={event => {
                                      event.stopPropagation();
                                      setEditAssetCardId(params.row.id);
                                  }}
                              />
                          ) : null,
                          canDeleteAssets ? (
                              <GridActionsCellItem
                                  key="delete"
                                  icon={
                                      <Tooltip title={assetT.tooltipDelete}>
                                          <DeleteIcon
                                              style={{
                                                  fontSize:
                                                      "calc(var(--body-font-size) * var(--font-size-multiplier))"
                                              }}
                                          />
                                      </Tooltip>
                                  }
                                  showInMenu
                                  label={assetT.tooltipDelete}
                                  onClick={event => {
                                      event.stopPropagation();
                                      deleteAssetById(
                                          params.row.id,
                                          params.row.asset_name,
                                          org.id,
                                          null,
                                          alert,
                                          () => t.actions
                                      );
                                  }}
                              />
                          ) : null,
                          canManageAssetSummary ? (
                              <GridActionsCellItem
                                  key="asset-summary-settings"
                                  icon={
                                      <Tooltip title="Device details fields">
                                          <SettingsSuggestIcon
                                              style={{
                                                  fontSize:
                                                      "calc(var(--body-font-size) * var(--font-size-multiplier))"
                                              }}
                                          />
                                      </Tooltip>
                                  }
                                  showInMenu
                                  label="Device details fields"
                                  onClick={event => {
                                      event.stopPropagation();
                                      setAssetSummarySettingsOpen(true);
                                  }}
                              />
                          ) : null
                      ].filter(Boolean)
              },
              ...baseColumns
          ]
        : baseColumns;

    /* ------------------------------------------------------------------ */
    /* DATA FETCH                                                          */
    /* ------------------------------------------------------------------ */

    const fetchAssets = useCallback(async () => {
        if (!org?.id) {
            setRows([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const res = await axios.get(
                apiUrl(`asset-card/by-organization?organization_id=${org.id}`)
            );
            if (res.data && Array.isArray(res.data.data)) {
                const preprocessedRows = res.data.data
                    .map(normalizeAssetCardListRow)
                    .map(row => {
                    const deviceNameValue = (row.Device?.name || "N/A");

                    const capacityRatingValue =
                        row.OperationalData?.capacity_rating !== undefined &&
                        row.OperationalData?.capacity_rating !== null
                            ? Number(row.OperationalData.capacity_rating)
                            : null;
                    const runningHoursCounterValue =
                        row.OperationalData?.running_hours_counter !== undefined &&
                        row.OperationalData?.running_hours_counter !== null
                            ? Number(row.OperationalData.running_hours_counter)
                            : null;
                    const purchaseCostValue =
                        row.FinancialOwnership?.purchase_cost !== undefined &&
                        row.FinancialOwnership?.purchase_cost !== null
                            ? Number(row.FinancialOwnership.purchase_cost)
                            : null;
                    const conditionStatusValue = (
                        row.MaintenanceLifecycle?.condition_status || ""
                    ).toLowerCase();

                    return {
                        ...row,
                        deviceName: deviceNameValue,
                        _originalDeviceName: row.Device?.name || "N/A",
                        capacity_rating: capacityRatingValue,
                        running_hours_counter: runningHoursCounterValue,
                        purchase_cost: purchaseCostValue,
                        condition_status: conditionStatusValue
                    };
                });
                setRows(preprocessedRows);
            } else {
                console.error(
                    "Received unexpected data format from /asset-card/by-organization:",
                    res.data
                );
                setRows([]);
                await showAlert("error", "Received an invalid format from the server.");
            }
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            await showAlert("error", errorMessage);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [org?.id, showAlert]);

    const handleAssetImportFile = useCallback(async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file || !org?.id) return;

        const formData = new FormData();
        formData.append("file", file);
        formData.append("organization_id", org.id);

        setImportingAssets(true);
        try {
            const res = await axios.post(apiUrl("asset-card/import"), formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            const summary = res.data?.data || {};
            const errorPreview = Array.isArray(summary.errors) && summary.errors.length > 0
                ? `\nSkipped rows: ${summary.errors.slice(0, 5).map((item) => `row ${item.row}: ${item.message}`).join("; ")}`
                : "";
            await showAlert(
                "info",
                `Asset import finished. Created: ${summary.created || 0}, Updated: ${summary.updated || 0}, Skipped: ${summary.skipped || 0}.${errorPreview}`
            );
            fetchAssets();
        } catch (err) {
            const importErrors = err.response?.data?.errors;
            if (Array.isArray(importErrors) && importErrors.length > 0) {
                const visibleErrors = importErrors.slice(0, 8).map((item) => {
                    const value = item.value === null || item.value === undefined || item.value === ""
                        ? "blank"
                        : `"${String(item.value).slice(0, 80)}"`;
                    return `Row ${item.row}, column ${item.field}: ${item.message} Received ${value}.`;
                });
                const remaining = importErrors.length - visibleErrors.length;
                const details = remaining > 0
                    ? `${visibleErrors.join(" ")} And ${remaining} more error(s).`
                    : visibleErrors.join(" ");
                await showAlert(
                    "error",
                    `${err.response?.data?.message || "The Excel file contains invalid data."} ${details}`
                );
            } else {
                await showAlert("error", getErrorMessage(err));
            }
        } finally {
            setImportingAssets(false);
        }
    }, [fetchAssets, org?.id, showAlert]);

    useEffect(() => {
        fetchAssets();
    }, [fetchAssets]);

    useEffect(() => {
        if (!org?.id || !canViewAssets) return undefined;
        let active = true;
        const checkForSheetChanges = async () => {
            try {
                const response = await axios.get(apiUrl("configuration/google-sheets/assets/version"));
                const nextVersion = Number(response.data?.data?.dataVersion || 0);
                if (active) setSheetSyncStatus(response.data?.data || { state: "idle", lastSyncedAt: null });
                if (active && sheetSyncVersionRef.current && nextVersion > sheetSyncVersionRef.current) {
                    fetchAssets();
                }
                sheetSyncVersionRef.current = nextVersion;
            } catch (_error) {
                // Asset cards remain usable if Google Sheets sync is not configured.
            }
        };
        checkForSheetChanges();
        const timer = window.setInterval(checkForSheetChanges, 5000);
        return () => { active = false; window.clearInterval(timer); };
    }, [canViewAssets, fetchAssets, org?.id]);

    useEffect(() => {
        if (!org?.id || !canViewAssets) return undefined;

        let mounted = true;
        axios
            .get(apiUrl("configuration/asset-card/device-details"), {
                params: { organization_id: org.id },
            })
            .then((res) => {
                if (mounted) {
                    setAssetPolicyAdjustment(res.data?.data?.deviceDetailsPolicyAdjustment || null);
                }
            })
            .catch((error) => {
                console.warn("Failed to check asset display policy", error);
            });

        return () => {
            mounted = false;
        };
    }, [canViewAssets, org?.id]);

    /* ------------------------------------------------------------------ */
    /* BULK ACTIONS                                                        */
    /* ------------------------------------------------------------------ */

    const [selectedRows, setSelectedRows] = React.useState([]);

    const selectActions = canDeleteAssets
        ? [
              {
                  title: "Delete",
                  icon: (
                      <DeleteIcon
                          style={{
                              color: "rgb(236, 69, 78)",
                              marginInlineEnd: "20px",
                              fontSize:
                                  "calc(var(--body-font-size) * var(--font-size-multiplier))"
                          }}
                      />
                  ),
                  action: async ids => {
                      await deleteMultiAssetCards(
                          selectedRows,
                          org.id,
                          fetchAssets,
                          alert, 
                          () => t.actions
                      );
                      setSelectedRows([]);
                  }
              }
          ].filter(Boolean)
        : [];

    /* ------------------------------------------------------------------ */
    /* ROW CLICK                                                           */
    /* ------------------------------------------------------------------ */

    const handleRowClick = (params, event) => {
        if (
            event?.target?.closest(".MuiDataGrid-cell--actions") ||
            event?.target?.closest('[role="menuitem"]')
        ) {
            return;
        }
        navigate("/asset-card/details/" + params.row.id);
    };

    /* ------------------------------------------------------------------ */
    /* RENDER                                                              */
    /* ------------------------------------------------------------------ */

    return canViewAssets ? (
        <div
            className="configuration devices-page asset-page"
            style={{ display: "flex", flexDirection: "column", height: "100%" }}
            dir={isRtl ? "rtl" : "ltr"}
        >
            <AppDialog
                open={dialog.open}
                type={dialog.type}
                variant={dialog.variant}
                icon={dialog.icon}
                title={dialog.title}
                message={dialog.message}
                confirmText={dialog.confirmText}
                cancelText={dialog.cancelText}
                showCancel={dialog.showCancel}
                onConfirm={() => closeDialog(true)}
                onCancel={() => closeDialog(false)}
                onClose={() => closeDialog(false)}
            />

            {addAssetCard && (
                <AddAssetCard
                    state={addAssetCard}
                    setState={setAddAssetCard}
                    refresh={fetchAssets}
                    onAlert={showAlert}
                />
            )}
            {editAssetCardId && (
                <EditAssetCard
                    assetId={editAssetCardId}
                    setState={setEditAssetCardId}
                    refresh={fetchAssets}
                    onAlert={showAlert}
                />
            )}
            {assetSummarySettingsOpen && (
                <AssetDeviceDetailsConfigModal
                    open={assetSummarySettingsOpen}
                    organizationId={org?.id}
                    onClose={() => setAssetSummarySettingsOpen(false)}
                    onAlert={showAlert}
                    onSaved={() => setAssetPolicyAdjustment(null)}
                />
            )}

            <div className="device-main">
                <div className="devices-header">
                    <div className="devices-header-left">
                        <span style={{ marginInlineEnd: 8 }}>
                            <AssetIcon color={"var(--accent)"} />
                        </span>
                        <h2 className="devices-title" style={{ color: "#06919f" }}>
                            {assetT.assetsPageTitle}
                        </h2>
                        {sheetSyncStatus.state !== "idle" ? (
                            <span
                                title={sheetSyncStatus.lastSyncedAt ? `Last synced ${new Date(sheetSyncStatus.lastSyncedAt).toLocaleString()}` : "Google Sheets sync"}
                                style={{
                                    marginInlineStart: 10,
                                    padding: "4px 8px",
                                    borderRadius: 999,
                                    fontSize: 11,
                                    fontWeight: 800,
                                    color: sheetSyncStatus.state === "error" ? "#b93838" : "#26734a",
                                    background: sheetSyncStatus.state === "error" ? "rgba(217,72,72,.11)" : "rgba(52,168,83,.12)"
                                }}
                            >
                                {sheetSyncStatus.state === "syncing" || sheetSyncStatus.state === "syncing_elsewhere"
                                    ? "Syncing sheet..."
                                    : sheetSyncStatus.state === "error" ? "Sheet sync issue" : "Sheets synced"}
                            </span>
                        ) : null}
                    </div>

                    {canCreateAssets ? (
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <input
                                ref={importInputRef}
                                type="file"
                                accept=".xlsx,.xls"
                                style={{ display: "none" }}
                                onChange={handleAssetImportFile}
                            />
                            <button
                                className="devices-primary-btn"
                                onClick={() => importInputRef.current?.click()}
                                disabled={importingAssets}
                                title="Import assets from Excel"
                            >
                                <FileUploadIcon style={{ fontSize: "1.1em" }} />
                                <span>{importingAssets ? "Importing..." : "Import"}</span>
                            </button>
                            <button
                                className="devices-primary-btn"
                                onClick={() => setAddAssetCard(true)}
                            >
                                <span>{assetT.newAsset}</span>
                            </button>
                        </div>
                    ) : null}
                </div>

                {assetPolicyAdjustment?.required ? (
                    <div className="asset-policy-adjustment-banner" role="alert">
                        <div>
                            <strong>Asset display settings need your attention</strong>
                            <span>
                                Administration changed the available fields or reduced the Device Details field limit to {assetPolicyAdjustment.allowedMaxItems}.
                                Only the first {assetPolicyAdjustment.allowedMaxItems} allowed fields from your previous choices are currently shown.
                            </span>
                        </div>
                        {canManageAssetSummary ? (
                            <button className="devices-primary-btn" type="button" onClick={() => setAssetSummarySettingsOpen(true)}>
                                Review settings
                            </button>
                        ) : null}
                    </div>
                ) : null}

                {loading ? (
                    <Spinner />
                ) : rows.length > 0 ? (
                    <div
                        className="devices-grid-wrapper"
                        style={{
                            flex: 1,
                            minHeight: 0,
                            marginTop: "20px"
                        }}
                    >
                        <DataGridTable
                            columns={columns}
                            rows={rows}
                            idcolumn={"id"}
                            pagingoptions={pagingoptions}
                            sortField="id"
                            sortType="asc"
                            handleRowClick={handleRowClick}
                            checkboxSelection={canDeleteAssets}
                            rowSelectionModel={selectedRows}
                            setRowSelectionModel={setSelectedRows}
                            selectActions={selectActions}
                            showToolbar={true}
                            tableKey="assetCards"
                        />
                    </div>
                ) : (
                    <div
                        style={{
                            width: "100%",
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize:
                                "calc(var(--body-font-size) * var(--font-size-multiplier))"
                        }}
                    >
                        {assetT.noData}
                    </div>
                )}
            </div>
        </div>
    ) : null;
};
